import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import AppFooter from '../components/AppFooter';
import {
  Shield, ArrowLeft, BookOpen, Plus, Trash2, Calendar, Heart, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MILESTONES = [
  'Uploaded my first credit report',
  'Sent my first dispute letter',
  'Received bureau response',
  'Item removed from my report',
  'Collection account resolved',
  'Paid off a debt',
  'Started my debt-free journey',
  'Credit score improved',
  'Completed my emergency fund',
  'Shared my testimony',
  'Other milestone'
];

export default function JournalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [milestone, setMilestone] = useState('');
  const [notes, setNotes] = useState('');
  const [scripture, setScripture] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { loadEntries(); }, []);

  const loadEntries = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/journal`);
      setEntries(data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!milestone) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/journal`, {
        milestone, notes: notes || null, scripture: scripture || null
      });
      toast.success('Milestone recorded!', { description: 'Your progress is being tracked.' });
      setMilestone(''); setNotes(''); setScripture(''); setShowForm(false);
      loadEntries();
    } catch (e) { toast.error('Failed to save entry'); }
    finally { setSubmitting(false); }
  };

  const deleteEntry = async (entryId) => {
    try {
      await axios.delete(`${API_URL}/api/journal/${entryId}`);
      toast.success('Entry removed');
      loadEntries();
    } catch (e) { console.error(e); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex flex-col" data-testid="journal-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">Selah Slate&trade;</span>
            </div>
            <Button variant="ghost" onClick={handleLogout} className="text-[#36454F]/70">Sign Out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        <Link to="/dashboard" className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />Back to Dashboard
        </Link>

        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#36454F]">Progress Journal</h1>
          <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
            "Dispute and breathe" — track your milestones and celebrate every step forward
          </p>
        </div>

        {/* Add Entry */}
        {!showForm ? (
          <Button onClick={() => setShowForm(true)} className="mb-6 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="add-journal-btn">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Record a Milestone
          </Button>
        ) : (
          <Card className="mb-6 border-[#36454F]/15 bg-white animate-fade-in-up">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F] flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />Record Your Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Milestone</Label>
                  <Select value={milestone} onValueChange={setMilestone}>
                    <SelectTrigger className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" data-testid="milestone-select">
                      <SelectValue placeholder="What did you accomplish?" />
                    </SelectTrigger>
                    <SelectContent>
                      {MILESTONES.map(m => <SelectItem key={m} value={m} className="font-['Manrope']">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Notes (optional)</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="How does this feel? What did you learn?" className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope'] min-h-[80px]" data-testid="journal-notes" />
                </div>
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">Scripture (optional)</Label>
                  <Input value={scripture} onChange={(e) => setScripture(e.target.value)} placeholder="e.g., Philippians 4:13" className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] rounded-sm font-['Manrope']" data-testid="journal-scripture" />
                </div>
                <div className="flex gap-3">
                  <Button type="submit" disabled={submitting || !milestone} className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm" data-testid="save-journal-btn">
                    {submitting ? 'Saving...' : 'Save Milestone'}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="text-[#36454F]/50 font-['Manrope']">Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Timeline */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-[#B76E79] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : entries.length === 0 ? (
          <Card className="border-[#36454F]/15 bg-white">
            <CardContent className="p-12 text-center">
              <BookOpen className="w-16 h-16 text-[#36454F]/15 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-2">Your Journey Starts Here</h3>
              <p className="text-[#36454F]/65 font-['Manrope'] max-w-md mx-auto">
                Record your first milestone — every step toward financial freedom matters.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-[#E0BFB8]/50"></div>
            
            <div className="space-y-4">
              {entries.map((entry, index) => (
                <div key={entry.entry_id} className="relative pl-14 animate-fade-in-up" style={{ animationDelay: `${index * 60}ms` }} data-testid={`journal-entry-${index}`}>
                  {/* Timeline dot */}
                  <div className="absolute left-4 top-4 w-5 h-5 rounded-full bg-[#B76E79] border-2 border-white shadow-sm flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-white"></div>
                  </div>
                  
                  <Card className="border-[#36454F]/10 bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-['Manrope'] font-semibold text-[#36454F]">{entry.milestone}</h4>
                          <p className="text-xs text-[#36454F]/40 font-['Manrope'] mt-0.5">
                            {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </p>
                        </div>
                        <button onClick={() => deleteEntry(entry.entry_id)} className="p-1 hover:bg-[#D9534F]/10 rounded text-[#36454F]/20 hover:text-[#D9534F] transition-colors" data-testid={`delete-entry-${index}`}>
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      </div>
                      {entry.notes && <p className="mt-2 text-sm text-[#36454F]/70 font-['Manrope']">{entry.notes}</p>}
                      {entry.scripture && (
                        <div className="mt-2 p-2 bg-[#E0BFB8]/10 rounded-sm">
                          <p className="text-xs text-[#36454F]/60 font-['Playfair_Display'] italic">{entry.scripture}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Encouragement */}
        <Card className="mt-8 border-[#E0BFB8] bg-[#E0BFB8]/5">
          <CardContent className="p-6 text-center">
            <Heart className="w-6 h-6 text-[#B76E79] mx-auto mb-2" strokeWidth={1.5} />
            <p className="font-['Playfair_Display'] text-sm italic text-[#36454F]">
              "Let us not become weary in doing good, for at the proper time we will reap a harvest if we do not give up."
            </p>
            <p className="text-xs text-[#36454F]/50 font-['Manrope'] mt-1">— Galatians 6:9</p>
          </CardContent>
        </Card>
      </main>
      <AppFooter />
    </div>
  );
}
